'use client';

import type { ReactElement } from 'react';
import { Paperclip, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AddDocument } from '@/components/shared/add-document';
import { useMatterFiles } from '@/lib/services/files/hooks';
import { filesApi } from '@/lib/services/files/client';

/**
 * The receipt evidencing that a contact paid this liability out of their own pocket.
 *
 * **Upload OR pick, not one or the other.** The field it writes
 * (`inventory_items.paid_by_evidence_file_id`) is documented as "a file already on the
 * matter", and a picker alone would have been the literal reading of it — but the lawyer
 * recording an out-of-pocket payment usually has the receipt in their inbox rather than
 * already filed. `AddDocument` is the house component that offers both from one control,
 * so this is a thin wrapper over it rather than a new uploader.
 *
 * Single-valued: a liability has one receipt, so `multiple` stays off and the first id
 * returned by either source wins.
 */
export function PaidByEvidenceField({
  matterId,
  value,
  onChange
}: {
  /** Absent in AI-creation mode, where no persisted matter file can be referenced yet. */
  matterId?: number;
  value: number | null;
  onChange: (fileId: number | null) => void;
}): ReactElement | null {
  // Without a matter there is nothing to upload against and nothing to pick from.
  const { data } = useMatterFiles(matterId ?? 0, undefined, {
    enabled: !!matterId
  });

  if (!matterId) return null;

  const attached = value
    ? data?.files?.find((file) => file.id === value)
    : undefined;

  return (
    <div>
      <Label className="text-muted-foreground text-xs">
        Receipt (optional)
      </Label>
      {value ? (
        <div className="mt-1 flex items-center gap-2">
          <Paperclip className="text-foreground-subtle h-3.5 w-3.5 shrink-0" />
          <span className="text-13px text-foreground min-w-0 flex-1 truncate">
            {/* The list can still be loading, or the file can sit outside the first
                page. Naming the id is worse than saying plainly that one is attached. */}
            {attached?.displayName ?? attached?.originalFilename ?? 'Attached'}
          </span>
          <Button
            type="button"
            variant="ghost"
            tone="muted"
            size="2xs"
            onClick={() => onChange(null)}
            aria-label="Remove receipt"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <div className="mt-1">
          <AddDocument
            matterId={matterId}
            sources={['upload', 'existing']}
            triggerLabel="Attach receipt"
            triggerIcon={Paperclip}
            triggerVariant="outline"
            triggerSize="sm"
            onUpload={async (files) => {
              const uploaded = await filesApi.uploadFile(
                matterId,
                files[0],
                'additional-document',
                { displayName: files[0].name }
              );
              return [uploaded.fileId];
            }}
            onPickExisting={(fileIds) => fileIds}
            onAttached={(fileIds) => {
              if (fileIds.length > 0) onChange(fileIds[0]);
            }}
          />
        </div>
      )}
    </div>
  );
}
